import { defineComponent, h, render, nextTick } from 'vue'
import LocalHighlight from '@/components/NoviceGuide/LocalHighlight/index.vue'
import { isString, isElement } from 'lodash'
import { isD2C } from '@/store'
import bus from '@/util/useBus'

// 生成一个唯一的key
const COMPONENT_CONTAINER_SYMBOL = Symbol('component_container')

// dom不在画面中时滚动到距离顶部的距离
const TO_TOP_HEIGHT = 250

// 当前正在显示的组件
let curCom: any = null

/**
 * 创建组件实例对象
 * 返回的实例和调用 getCurrentComponent() 返回的一致
 * @param {*} Component
 */
function createComponent (Component: any, props: any, children: any) {
  // 创建vnode
  const vnode: any = h(Component, { ...props }, children)
  // 创建组件容器
  const container = document.createElement('div')
  // @ ts-ignore 将组件容器挂载到vnode上，方便后续移除
  vnode[COMPONENT_CONTAINER_SYMBOL] = container
  // 将vnode渲染到组件容器内, 在 vue2 的版本中，父级元素是可以传 null 的，但是 vue3 不支持
  render(vnode, container)
  // 返回组件实例
  return vnode.component
}

/**
 * 销毁组件实例对象
 * @param {*} ComponnetInstance 通过createComponent方法得到的组件实例对象
 */
export function unmountComponent (ComponnetInstance: any) {
  // 移除组件节点，render函数的第一个传null，表示为移除动作，会执行unmount方法
  render(null, ComponnetInstance.vnode[COMPONENT_CONTAINER_SYMBOL])
}

// ......
// ......

// 当前场景下是可以省略这一步转换，但是，就类型而言，defineComponent 返回的值有一个合成类型的构造函数
// const componentConstructor = defineComponent(LocalHighlight)

// 创建一个变量接收创建的组件实例
let instance: any

// 创建节点
const showHighLightComponent = (options: any) => {
  // 创建组件实例对象
  instance = createComponent(LocalHighlight, options, null)
  curCom = instance
  // 添加到body
  document.body.appendChild(instance.vnode.el)
}

// 处理传入的options
const disposeOptions = (options: any) => {
  const modeObj = disposeMode(options)
  options.popover = disposePopover(options)
  return { ...options, ...modeObj }
}

// 根据mode模式处理数据
const disposeMode = (options: any) => {
  const { mode, element, anchorPosition, offsetX, offsetY, extraWidth, extraHeight, isElFixed } = options
  if (isElFixed) {
    scrollToSpot(0)
  }
  if (!mode || mode === '' || mode === 'element') {
    return disposeModeElement(element, offsetX, offsetY, extraWidth, extraHeight, isElFixed)
  } else
  if (mode === 'anchor') {
    return disposeModeAnchor(element, anchorPosition, offsetX, offsetY, extraWidth, extraHeight, isElFixed)
  }
}

// element模式处理
const disposeModeElement = (element: any, offsetX: any = 0, offsetY: any = 0, extraWidth: any = 0, extraHeight: any = 0, isElFixed: any = false) => {
  let dom: any = null
  if (isString(element)) {
    dom = document.querySelector(element)
  } else
  if (isElement(element)) {
    dom = element
  }
  if (!dom) {
    return
  }
  const el: any = document.querySelector('#app .el-scrollbar__wrap')
  const domTop = getDomAbsTop(dom)
  const domLeft = dom.getBoundingClientRect().left
  if (!isInView(dom)) {
    scrollToSpot(domTop - TO_TOP_HEIGHT)
  }
  return {
    offsetTop: domTop - el.scrollTop + offsetY,
    offsetLeft: domLeft + offsetX,
    boxWidth: dom.offsetWidth + extraWidth,
    boxHeight: dom.offsetHeight + extraHeight
  }
}

// anchor模式处理
const disposeModeAnchor = (element: any, anchorPosition: any, offsetX: any = 0, offsetY: any = 0, extraWidth: any = 0, extraHeight: any = 0, isElFixed: any = false) => {
  let dom: any = null
  if (isString(element)) {
    dom = document.querySelector(element)
  } else
  if (isElement(element)) {
    dom = element
  }
  if (!dom) {
    return
  }
  const el: any = document.querySelector('#app .el-scrollbar__wrap')
  const domTop = getDomAbsTop(dom)
  const domLeft = dom.getBoundingClientRect().left
  if (!isInView(dom)) {
    scrollToSpot(domTop - TO_TOP_HEIGHT)
  }
  const left = domLeft + offsetX
  const top = domTop + offsetY
  if (!anchorPosition || anchorPosition === '' || anchorPosition === 'tl') {
    return {
      offsetTop: top - el.scrollTop,
      offsetLeft: left
    }
  } else
  if (anchorPosition === 'tr') {
    return {
      offsetTop: top - el.scrollTop,
      offsetLeft: left + dom.offsetWidth
    }
  } else
  if (anchorPosition === 'bl') {
    return {
      offsetTop: top - el.scrollTop + dom.offsetHeight,
      offsetLeft: left
    }
  } else
  if (anchorPosition === 'br') {
    return {
      offsetTop: top - el.scrollTop + dom.offsetHeight,
      offsetLeft: left + dom.offsetWidth
    }
  } else
  if (anchorPosition === 'center') {
    return {
      offsetTop: top - el.scrollTop + (dom.offsetHeight / 2),
      offsetLeft: left + (dom.offsetWidth / 2)
    }
  }
}

// 处理popover参数
const disposePopover = (options: any) => {
  const disposeObj = { ...options.popover }
  if (!disposeObj.position || disposeObj.position === '') {
    disposeObj.position = 'bottom-right'
  }
  if (!disposeObj.offsetX) {
    disposeObj.offsetX = 0
  }
  if (!disposeObj.offsetY) {
    disposeObj.offsetY = 0
  }
  return disposeObj
}

// 滚动条滚动到指定位置
const scrollToSpot = (val: any) => {
  // const el = document.querySelector('#app .el-scrollbar__wrap')
  // el && el.scroll(0, val)
  bus.emit('elScrollTo', val)
}

// 检查当前元素在视口中是否可见
const isInView = (dom: any) => {
  let top = dom.offsetTop
  let left = dom.offsetLeft
  const width = dom.offsetWidth
  const height = dom.offsetHeight

  let el = dom

  while (el.offsetParent) {
    el = el.offsetParent
    top += el.offsetTop
    left += el.offsetLeft
  }

  return (
    // top >= window.pageYOffset &&
    // left >= window.pageXOffset &&
    (top + height) <= (window.pageYOffset + window.innerHeight) &&
    (left + width) <= (window.pageXOffset + window.innerWidth)
  )
}

// 计算dom绝对高度
const getDomAbsTop = (dom: any) => {
  let top = dom.offsetTop
  let el = dom

  while (el.offsetParent) {
    el = el.offsetParent
    top += el.offsetTop
  }
  return top
}

// options为组件的props
// 可传入参数
// element  String/Dom id或者dom对象
// isElFixed  元素是否fixed固定在屏幕上  默认false
// mode  String 选择区域模式 element-寻找元素 anchor-元素锚点定位 free-自由框选(预留，没写) 默认 element
// padding Number 高亮区域边距（圆角大小随边距变化）
// allowClose Boolean 是否允许点击遮罩关闭
// anchorPosition String 元素锚点模式下取的元素位置 tl tr bl br center 默认 tl  需要传boxWidth boxHeight
// offsetX Number X轴偏移量
// offsetY Number Y轴偏移量
// imgUrl String 以锚点为起点放置的图片
// imgRightDistance Number 图片最大延展到距离右边的距离
// popover Object 弹窗对象
// extraWidth Number 高亮区域的额外宽度(只在element模式生效) 默认0
// extraHeight Number 高亮区域的额外高度(只在element模式生效)  默认0
// // description String 弹窗内容
// // position String 弹窗定位 top-left top-right bottom-left bottom-right left-top left-bottom right-top tight-bottom mid 默认 bottom-right
// // popoverWidth Number 弹窗宽度 默认 500
// // offsetX Number 弹窗X轴偏移量 默认 0
// // offsetY Number 弹窗Y轴偏移量 默认 0
// iconWidth Number icon小青蛙的宽度 默认72
export const highLight = function (options: any, stepsObj?: any) {
  const close = options.onClose
  // 重新封装close，添加移除元素操作
  options.onClose = () => {
    close && close.call()
    unmountComponent(instance)
  }
  options.curStep = stepsObj.curStep
  options.totalStep = stepsObj.totalStep
  options.handleStep = stepsObj.stepCallBack
  options.isSkip = stepsObj.isSkip
  // 处理传入的option
  options = disposeOptions(options)
  showHighLightComponent(options)
}

// 分步引导
// defineSteps config
// isSkip Boolean 是否可跳过 默认true
// nextCallback Function 下一步回调
// skipCallback Function 跳过回调
// beforeGuide Function 引导开始前回调
export const defineSteps = (steps: any, config?: any) => {
  if (curCom) {
    unmountComponent(curCom)
  }
  let index = 0
  const stepConfig = {
    isSkip: config?.isSkip
  }
  // 引导开始前回调
  config.beforeGuide()
  nextTick(() => {
    const changeSteps = (key: any, step?: any) => {
      index = step
      if (key === 'next') {
        config.nextCallback(index)
        nextTick(() => {
          // 下一步
          highLight(steps[index], {
            curStep: index,
            totalStep: steps.length,
            stepCallBack: changeSteps,
            ...stepConfig
          })
        })
      }
      if (key === 'skip') {
        config.skipCallback()
        // 跳过
        unmountComponent(instance)
      }
    }
    if (steps.length !== 0) {
      config?.nextCallback && config.nextCallback(index)
      highLight(steps[index], {
        curStep: index,
        totalStep: steps.length,
        stepCallBack: changeSteps,
        ...stepConfig
      })
    }
  })
}
